// mixins/UltraStableAudioPlayer.js
export default {
  methods: {
    async setAudio (url, waitEnd = false) {
      console.log('setAudio: ' + url)
      if (!url) return
      await this.ensureAudioContext()

      try {
        if (!this.sound_effect) {
          this.sound_effect = new Audio()
          this.sound_effect.preload = 'auto'
          try { this.sound_effect.playsInline = true } catch (e) {}
          try { this.sound_effect.setAttribute && this.sound_effect.setAttribute('playsinline', '') } catch (e) {}
        }

        await this.audio_stop()

        this.sound_effect.src = url
        this.sound_effect.load()

        const playAttempt = async (retries = 1) => {
          try {
            const p = this.sound_effect.play()
            if (p !== undefined) {
              await p
            }
            return true
          } catch (err) {
            console.warn('[setAudio] play failed, retries left', retries, err)

            if (retries > 0) {
              await new Promise(r => setTimeout(r, 250))
              try { await this.ensureAudioContext(1500) } catch (e) {}
              return playAttempt(retries - 1)
            } else {
              console.warn('[setAudio] 自動重試失敗，準備呼叫強制互動 UI')

              this.isTimerPaused = true

              return new Promise(resolveUI => {
                const overlay = document.createElement('div')
                overlay.style.cssText = `
                  position: fixed;
                  top: 0; left: 0; right: 0; bottom: 0;
                  background: rgba(0, 0, 0, 0.85);
                  color: white;
                  display: flex;
                  flex-direction: column;
                  justify-content: center;
                  align-items: center;
                  z-index: 99999;
                  font-size: 1.5rem;
                  font-weight: bold;
                  cursor: pointer;
                `
                overlay.innerHTML = `
                  <p class="mb-3" style="font-size: 3rem;">⏸️</p>
                  <p>音訊被瀏覽器暫停</p>
                  <p style="font-size: 1.2rem; margin-top: 10px; color: #4facfe;">請點擊畫面任意處繼續</p>
                `
                document.body.appendChild(overlay)

                overlay.addEventListener('click', async () => {
                  overlay.remove()
                  this.isTimerPaused = false
                  try {
                    await this.audioContext.resume()
                    await this.sound_effect.play()
                    console.log("[setAudio] ✅ 手動解鎖播放成功！")
                    resolveUI(true)
                  } catch (finalErr) {
                    console.error("[setAudio] 強制互動後依然失敗", finalErr)
                    resolveUI(false)
                  }
                }, { once: true })
              })
            }
          }
        }

        const ok = await playAttempt(1)
        if (!ok) {
          console.warn('[setAudio] final play failed for', url)
          return
        }

        if (!waitEnd) {
          return
        }

        return new Promise((resolve, reject) => {
          const audio = this.sound_effect
          const clean = () => {
            audio.onended = null
            audio.onerror = null
          }
          audio.onended = () => {
            clean()
            resolve()
          }
          audio.onerror = (e) => {
            clean()
            console.warn('[setAudio] audio error', e)
            resolve()
          }
        })
      } catch (e) {
        console.warn('[setAudio] 播放例外', e)
      }
    },

    async playAudio({ url, repeat = 1, withBeep = false }) {
      if (!url) return
      console.log('playAudio: ' + url)

      await this.ensureAudioContext()

      return new Promise(resolve => {
        let audio2 = new Audio()
        audio2.src = url
        audio2.preload = "auto"
        audio2.playsInline = true

        let playCount = 0
        let isStarted = false

        const finalResolve = async () => {
          audio2.onended = null
          audio2.onerror = null

          if (withBeep) {
            try { await this.playBeep() } catch {}
          }

          resolve()
        }

        const safePlay = () => {
          if (isStarted) return
          isStarted = true

          try { audio2.currentTime = 0 } catch {}

          const p = audio2.play()
          if (p && p.catch) {
            p.catch(async err => {
              console.warn("iOS play failed, retrying", err)
              try {
                await this.audioContext.resume()
                await audio2.play()
              } catch (e) {
                console.warn("自動重試失敗，準備呼叫強制互動 UI", e)
                this.isTimerPaused = true

                const overlay = document.createElement('div')
                overlay.style.cssText = `
                  position: fixed;
                  top: 0; left: 0; right: 0; bottom: 0;
                  background: rgba(0, 0, 0, 0.85);
                  color: white;
                  display: flex;
                  flex-direction: column;
                  justify-content: center;
                  align-items: center;
                  z-index: 99999;
                  font-size: 1.5rem;
                  font-weight: bold;
                  cursor: pointer;
                `
                overlay.innerHTML = `
                  <p class="mb-3" style="font-size: 3rem;">⏸️</p>
                  <p>音訊被瀏覽器暫停</p>
                  <p style="font-size: 1.2rem; margin-top: 10px; color: #4facfe;">請點擊畫面任意處繼續測驗</p>
                `
                document.body.appendChild(overlay)

                overlay.addEventListener('click', async () => {
                  overlay.remove()
                  this.isTimerPaused = false
                  try {
                    await this.audioContext.resume()
                    await audio2.play()
                    console.log("✅ 手動解鎖播放成功！")
                  } catch (finalErr) {
                    console.error("強制互動後依然失敗 → 跳過本題音檔", finalErr)
                    finalResolve()
                  }
                }, { once: true })
              }
            })
          }
        }

        audio2.onended = () => {
          playCount++
          if (playCount < repeat) {
            isStarted = false
            safePlay()
          } else {
            finalResolve()
          }
        }

        audio2.onerror = () => {
          console.warn("audio error → skip")
          finalResolve()
        }

        audio2.addEventListener("loadeddata", () => safePlay(), { once: true })

        setTimeout(() => {
          if (!isStarted) {
            console.warn("iOS fallback: force play")
            safePlay()
          }
        }, 400)
      })
    },
    async UltraStableAudioPlayer ({
      url,
      repeat = 1,
      beep = false,
      singleton = false,
      waitEnd = false,
    }) {
      if (!url) return
      await this.ensureAudioContext()

      let audio
      if (singleton) {
        if (!this._singletonAudio) {
          this._singletonAudio = new Audio()
          this._singletonAudio.preload = "auto"
          try { this._singletonAudio.playsInline = true } catch {}
          try { this._singletonAudio.setAttribute("playsinline", "") } catch {}
        }
        audio = this._singletonAudio

        try {
          audio.pause()
          audio.currentTime = 0
        } catch {}
      } else {
        audio = new Audio()
        audio.preload = "auto"
        try { audio.playsInline = true } catch {}
        try { audio.setAttribute("playsinline", "") } catch {}
      }

      audio.src = url
      audio.load()

      return new Promise(async (resolve) => {
        let playCount = 0
        let started = false
        const SAFE_TIMEOUT = 400

        const cleanup = () => {
          audio.onerror = null
          audio.onended = null
        }

        const finalResolve = async () => {
          cleanup()
          if (beep) {
            try { await this.playBeep() } catch {}
          }
          resolve()
        }

        const safePlay = async () => {
          if (started) return
          started = true

          try { audio.currentTime = 0 } catch {}

          try {
            await audio.play()
          } catch (e) {
            console.warn("play failed → retry", e)

            try {
              await this.audioContext.resume()
              await audio.play()
            } catch (e2) {
              console.warn("final play failed", e2)
              return finalResolve()
            }
          }
        }

        audio.onended = () => {
          playCount++
          if (playCount < repeat) {
            started = false
            safePlay()
          } else {
            finalResolve()
          }
        }

        audio.onerror = () => {
          console.warn("audio error")
          finalResolve()
        }

        audio.addEventListener("loadeddata", () => safePlay(), { once: true })

        setTimeout(() => {
          if (!started) {
            console.warn("force play (iOS fallback)")
            safePlay()
          }
        }, SAFE_TIMEOUT)
        if (!waitEnd) resolve()
      })
    },
async jumpToPart (index) {
        if (this.currentPart === index) return
        console.log(`Jumping to Part ${index + 1}...`)
        
        this.sessionToken++ // 刷新 Token，宣告舊流程作廢
        if (this.timer) {
          clearInterval(this.timer)
          this.timer = null
        }
        await this.audio_stop()

        this.currentPart = index
        this.currentQuestionIndex = 0

        if (this.selectedStep === 0) {
          if (!this.isToeic) this.currentPhase = 'preContinue'
          return
        }

        await this.ensureAudioContext()
        if (this.isToeic) {
          this.currentPhase = 'direction'
          this.start_exam()
        } else {
          this.currentPhase = 'preContinue'
        }
      },

      async safePlayAudio(options) {
        const currentToken = this.sessionToken;
        await this.playAudio(options);
        return new Promise(resolve => {
          if (this.sessionToken === currentToken) resolve();
        });
      },

      async safeSetAudio(url, waitEnd = false) {
        const currentToken = this.sessionToken;
        await this.setAudio(url, waitEnd);
        return new Promise(resolve => {
          if (this.sessionToken === currentToken) resolve();
        });
      },
  }
}
